import { useCallback } from 'react';
import { DateFormatter, parseDate } from '@internationalized/date';
import { useCalendarViewMode } from './useCalendarViewMode';

export function useDateLabelFormatter(selectedValue: string) {
  const mode = useCalendarViewMode(selectedValue);

  const formatDateLabel = useCallback(
    (dateFrom: string, dateTo: string): string => {
      try {
        // Parse dates safely using @internationalized/date
        const startCalendarDate = parseDate(dateFrom);
        const endCalendarDate = parseDate(dateTo);

        // Convert to native Date for formatting
        const startDate = startCalendarDate.toDate('UTC');
        const endDate = endCalendarDate.toDate('UTC');

        if (mode === 'Monthly') {
          const yearFormatter = new DateFormatter('en-GB', {
            month: 'long',
            year: 'numeric',
          });
          return yearFormatter.format(startDate);
        }

        // Check if dates span different months
        const sameMonth =
          startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();

        if (sameMonth) {
          // Same month: "13-19 Oct 2025"
          const dayFormatter = new DateFormatter('en-GB', {
            day: 'numeric',
          });
          const monthYearFormatter = new DateFormatter('en-GB', {
            month: 'short',
            year: 'numeric',
          });

          const startDay = dayFormatter.format(startDate);
          const endDay = dayFormatter.format(endDate);
          const monthYear = monthYearFormatter.format(endDate);

          return `${startDay}-${endDay} ${monthYear}`;
        } else {
          // Different months: "28 Sep - 4 Oct 2025"
          const formatter = new DateFormatter('en-GB', {
            month: 'short',
            day: 'numeric',
          });
          const yearFormatter = new DateFormatter('en-GB', {
            year: 'numeric',
          });

          return `${formatter.format(startDate)} - ${formatter.format(endDate)} ${yearFormatter.format(endDate)}`;
        }
      } catch {
        return `${dateFrom} - ${dateTo}`;
      }
    },
    [mode]
  );

  return formatDateLabel;
}
